import { useEffect, useRef, useState } from 'react';
import type { CurrentUser } from '../api';
import { Avatar } from './ui';
import { ThemeToggle } from './ThemeToggle';
import { IconLogout } from './icons';

const ROLE_LABEL: Record<CurrentUser['role'], string> = {
  PENDING: 'Pending access',
  REQUESTER: 'Requester',
  APPROVER: 'Approver',
  ADMIN: 'Administrator',
  SUPER_ADMIN: 'Super Admin',
  SYSTEM_ADMIN: 'System administrator',
  MTN_TECHNICIAN: 'MTN Technician',
  APPROVED_VIEWER: 'Approved requests viewer',
};

/**
 * Identity, theme and sign-out behind the avatar.
 *
 * The header had eleven things competing along one line and was clipping the
 * navigation to fit them. Everything here is occasional: you change your theme
 * once and sign out at the end of the day, so it does not need to be on screen
 * the whole time. What is left in the bar is what people actually use.
 */
export function AccountMenu({ user, onSignOut }: { user: CurrentUser; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Account: ${user.displayName}`}
        className="flex items-center gap-2 rounded-full p-0.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        <Avatar name={user.displayName} />
      </button>

      {open && (
        <div
          role="menu"
          className="animate-pop absolute right-0 z-30 mt-2 w-64 origin-top-right overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <Avatar name={user.displayName} />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-slate-900 dark:text-white">
                {user.displayName}
              </div>
              <div className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</div>
              <div className="mt-0.5 text-xs font-medium text-brand-700 dark:text-brand-300">
                {ROLE_LABEL[user.role]}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <span className="text-sm text-slate-700 dark:text-slate-300">Theme</span>
            <ThemeToggle />
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <IconLogout className="h-4 w-4" />
            Sign out
          </button>

          {/* Name only. The cube is a 3D render and 16px turns it into a red
              blob with spikes; the sidebar footer is where it gets the room. */}
          <div className="border-t border-slate-100 px-4 py-2.5 dark:border-slate-800">
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              Built by Micro Tech Namibia
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
